export interface SeoHeadHookProps {
  blogSchema: ArticleSchemaProps;
  breadcrumbSchema: BreadcrumbListSchemaProps;
  metaDescription: string;
  noIndex?: boolean;
  title: string;
}

export enum SCHEMA_TYPE {
  BLOG = 'Blog',
  BLOG_POSTING = 'BlogPosting',
  BREADCRUMB_LIST = 'BreadcrumbList',
}

export interface SeoHeadHook {
  createBlogPostSchema: (props: ArticleSchemaProps) => BlogPostingSchema;
  createBreadcrumbListSchema: (
    props: BreadcrumbListSchemaProps,
  ) => BreadcrumbListSchema;
  createGeneralSchema: () => BlogSchema;
  createTenerifeGraphSchema: () => TenerifeGraphSchema;
  currentUrl: string;
  description: string;
  robots: string;
}

export interface SeoHeadProps {
  blogSchema?: ArticleSchemaProps;
  breadcrumbSchema?: BreadcrumbListSchemaProps;
  image?: string;
  metaDescription?: string;
  noIndex?: boolean;
  tenerifeSchema?: boolean;
  title: string;
}

export interface ArticleSchemaProps {
  dateCreated: string;
  dateModified: string;
  datePublished: string;
  description: string;
  keywords: string[];
  mainImage: string;
  title: string;
  url: string;
}

export interface BreadcrumbListSchemaProps {
  breadcrumbElements?: {
    breadcrumbUrl?: string;
    title: string;
  }[];
}

export interface BaseSchema {
  '@context': string;
  '@type': SCHEMA_TYPE;
}

export interface BlogPostingSchema extends BaseSchema {
  author: {
    '@type': string;
    name: string;
    url: string;
  }[];
  dateCreated: string;
  dateModified: string;
  datePublished: string;
  description: string;
  headline: string;
  image: string[];
  inLanguage: string;
  keywords: string[];
  name: string;
  url: string;
}

export interface BreadcrumbListSchema extends BaseSchema {
  itemListElement: {
    '@type': 'ListItem';
    item?: string;
    name: string;
    position: number;
  }[];
}

export interface BlogSchema extends BaseSchema {
  '@id': string;
  about: string;
  alternateName: string;
  description: string;
  inLanguage: string;
  isPartOf: {
    '@id': string;
  };
  name: string;
  potentialAction: {
    '@type': 'SearchAction';
    'query-input': 'required name=search_term_string';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: 'https://foxparadox.pl/szukaj/?q={search_term_string}';
    };
  };
  url: string;
}

export interface UnitPriceSpecification {
  '@type': string;
  price: string;
  priceCurrency: string;
  referenceQuantity: {
    '@type': string;
    unitCode: string;
    value: number;
  };
}

export interface OrganizationSeller {
  '@type': string;
  name: string;
}

export interface Offer {
  '@type': string;
  availability: string;
  description: string;
  name: string;
  price?: string;
  priceCurrency: string;
  priceSpecification?: UnitPriceSpecification;
  priceValidUntil?: string;
  seller: OrganizationSeller;
  url: string;
}

export interface OfferBuyAction {
  '@type': string;
  target: string;
}

export interface ProductOffer extends Offer {
  potentialAction?: OfferBuyAction;
  shippingDetails?: OfferShippingDetails;
}

export interface AggregateRating {
  '@type': string;
  ratingValue: string;
  reviewCount: string;
}

export interface ReviewRating {
  '@type': string;
  bestRating: string;
  ratingValue: string;
}

export interface PersonAuthor {
  '@type': string;
  name: string;
}

export interface Review {
  '@type': string;
  author: PersonAuthor;
  datePublished: string;
  reviewBody: string;
  reviewRating: ReviewRating;
}

export interface DeliveryChargeSpecification {
  '@type': string;
  currency: string;
  value: string;
}

export interface DefinedRegion {
  '@type': string;
  addressCountry: string;
}

export interface OfferShippingDetails {
  '@type': string;
  shippingDestination: DefinedRegion;
  shippingRate: DeliveryChargeSpecification;
}

export interface MerchantReturnPolicy {
  '@type': string;
  applicableCountry?: string;
  merchantReturnDays?: number;
  returnFees?: string;
  returnMethod?: string;
  returnPolicyCategory: string;
}

export interface TenerifeBookNode {
  '@id': string;
  '@type': string;
  aggregateRating?: AggregateRating;
  alternateName?: string;
  author: PersonAuthor;
  bookFormat?: string;
  datePublished: string;
  description: string;
  image: string;
  inLanguage: string;
  isAccessibleForFree: boolean;
  isbn?: string;
  name: string;
  publisher: {
    '@type': string;
    name: string;
    url: string;
  };
  review?: Review[];
  url: string;
}

export interface TenerifeProductNode {
  '@id': string;
  '@type': string;
  aggregateRating?: AggregateRating;
  brand: OrganizationSeller;
  description: string;
  image: string;
  isRelatedTo: { '@id': string };
  name: string;
  offers: ProductOffer[];
  review?: Review[];
  sku: string;
  url: string;
}

export interface TenerifeGraphSchema {
  '@context': string;
  '@graph': [TenerifeBookNode, TenerifeProductNode];
}
